import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Heading, Icon, useToast } from "@elaniintc/goes-design-system";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetTemplate,
  useGetTemplateVersions,
  useUpdateTemplate,
} from "@/queries/templates/templates.api";
import TemplateEditor from "./TemplateEditor";
import type { DocumentDetails, SlotType } from "@/types/entities/templates";
import DocumentModal from "./NewDocumentTemplateModal";
import { useQueryClient } from "@tanstack/react-query";
import type { Editor } from "@tiptap/react";
import {
  extractSlotsWithMetadata,
  normalizeHTML,
  transformSlotsFromBackendFormat,
  transformSlotsToBackendFormat,
} from "../utils/slot-transformations";
import { useTemplateLoader } from "../hooks/useTemplateLoader";

export default function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [templateName, setTemplateName] = useState("");
  const [description, setDescription] = useState("");
  const [richTextContent, setRichTextContent] = useState("");
  const [, setUsedSlots] = useState<Map<string, SlotType>>(new Map());
  //const [fileName, setFileName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [initialContent, setInitialContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string>("");

  const { data: template, isLoading } = useGetTemplate(id!);

  const parentTemplateId =
    template?.data.parent_template_id || template?.data.id;
  const { data: versionsData } = useGetTemplateVersions(parentTemplateId);

  const { mutate: updateTemplate, isPending: isSaving } = useUpdateTemplate();

  const editorRef = useRef<Editor | null>(null);

  const versionOptions = useMemo(() => {
    if (!versionsData?.data) return [];
    return versionsData.data
      .sort((a, b) => b.version - a.version)
      .map((version) => ({
        value: version.id,
        label: `v${version.version}`,
      }));
  }, [versionsData]);

  useTemplateLoader({
    richText: template?.data.rich_text,
    onContentLoaded: (content) => {
      setRichTextContent(content);
      setInitialContent(content);
    },
    onSlotsExtracted: (slots) => {
      setUsedSlots(slots);
    },
    editorRef,
    templateType: "document",
  });

  const isValid = useMemo(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(richTextContent, "text/html");
    const el = doc.body.firstElementChild;

    if (!el) return false;

    const text = el?.textContent?.trim();

    if (!text) return false;

    return text.length > 0 || el.children?.length > 0;
  }, [richTextContent]);

  useEffect(() => {
    if (template?.data) {
      setTemplateName(template.data.display_name);
      setDescription(template.data.description || "");
      setSelectedVersion(template.data.id);
    }
    if (template?.data.rich_text) {
      const transformedContent = transformSlotsFromBackendFormat(
        template.data.rich_text,
      );

      setRichTextContent(transformedContent);
      setInitialContent(transformedContent);
    }
  }, [template, id]);

  useEffect(() => {
    const hasContent =
      richTextContent.trim() !== "" || initialContent.trim() !== "";

    if (hasContent) {
      const normalizedCurrent = normalizeHTML(richTextContent);
      const normalizedInitial = normalizeHTML(initialContent);

      setHasUnsavedChanges(normalizedCurrent !== normalizedInitial);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [richTextContent, initialContent]);

  const handleSlotsChange = useCallback((slots: Map<string, SlotType>) => {
    setUsedSlots(slots);
  }, []);

  const handleSave = () => {
    if (!id) return;

    const transformedRichText = transformSlotsToBackendFormat(richTextContent);
    const variables = extractSlotsWithMetadata(richTextContent);

    const variablesConfig = variables.map((variable) => ({
      ...variable,
      document_generator_template_id: template?.data.id || id,
    }));

    updateTemplate(
      {
        id,
        data: {
          display_name: templateName,
          description: description,
          mapping_name: template?.data.mapping_name || "simple",
          rich_text: transformedRichText,
          variables: variablesConfig,
        },
      },
      {
        onSuccess: () => {
          setInitialContent(richTextContent);
          setHasUnsavedChanges(false);

          showToast({
            title: "Plantilla guardada",
            description: "Los cambios se guardaron correctamente.",
            options: { type: "success", duration: 4000 },
          });
          navigate("/plantillas");
        },
        onError: (error) => {
          console.error("Error al guardar:", error);
          showToast({
            title: "Error al guardar",
            description: "No se pudo guardar la plantilla.",
            options: { type: "error", duration: 4000 },
          });
        },
      },
    );
  };

  const handleVersionChange = (versionId: string) => {
    setSelectedVersion(versionId);
    navigate(`/plantillas/documentos/${versionId}`, { replace: true });
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["template", id],
    });

    setIsModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-body">Cargando...</p>
      </div>
    );
  }

  const templateDetails: DocumentDetails | undefined = template
    ? {
        template_id: template.data.id,
        display_name: template.data.display_name,
        description: template.data.description || "",
        status: template.data.status,
        rich_text: template.data.rich_text || "",
      }
    : undefined;

  return (
    <>
      <DocumentModal
        isOpen={isModalOpen}
        template={templateDetails}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onSuccess={handleModalSuccess}
      />
      <div className="border border-neutral shadow-custom rounded-xl mt-8 flex flex-col overflow-hidden">
        <div className="flex justify-between items-start gap-3 border-b border-neutral w-full p-3">
          <div className="flex justify-start gap-2.5 items-center flex-auto truncate">
            <Button
              size="default"
              variant="quiet"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              <Icon type="far" name="edit" className="text-current" />
            </Button>
            {isLoading ? (
              <div className="flex flex-col">
                <Skeleton className="h-4 w-48 mb-2" />
                <Skeleton className="h-3 w-64" />
              </div>
            ) : (
              <div className="flex flex-col justify-center max-h-10 truncate w-full">
                <Heading
                  variant="h6"
                  className="text-body-loud w-full truncate"
                  title={templateName || "Nombre de la plantilla"}
                >
                  {templateName || "Nombre de la plantilla"}
                </Heading>
                <p
                  className="text-body-quiet -translate-y-[3px] text-sm w-full truncate"
                  title={description || ""}
                >
                  {description || ""}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 h-10">
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges || !isValid}
              className="flex items-center gap-2"
            >
              Guardar
              <Icon type="far" name="save" className="text-sm" />
            </Button>
          </div>
        </div>

        <TemplateEditor
          value={richTextContent}
          onChange={setRichTextContent}
          placeholder="Escribe el contenido de la plantilla..."
          minHeight="calc(100vh - 250px)"
          maxHeight="calc(100vh - 250px)"
          showSlots={true}
          showHeadings={true}
          showAlignment={true}
          showIndent={false}
          integrated={true}
          templateType="document"
          //fileNameValue={fileName}
          //onFileNameChange={setFileName}
          onSlotsChange={handleSlotsChange}
          versiones={versionOptions}
          selectedVersion={selectedVersion}
          onVersionChange={handleVersionChange}
          editorRef={editorRef}
        />
      </div>
    </>
  );
}
